import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography, Box, Button } from "@mui/material";
import Pagination from "components/Pagination/Pagination";
import FilterSidebar from "components/FilterSidebar/FilterSidebar";
import SearchBar from "components/SearchBar/SearchBar";
import { useDispatch, useSelector } from "react-redux";
import { fetchDataRequest } from "../../redux/api/apiSlice";
import {
  resetFiltersAndSearch,
  searchQueryChanged,
  setSelectedFilters,
} from "../../redux/searchAndFilter/searchAndFilterSlice";
import styles from "../Datasets/Datasets.module.css";

const DATASET_NAME = "Repository of eu projects";

const ProjectList = () => {
  const dispatch = useDispatch();
  const { datasets, loading, error } = useSelector((state) => state.api); // Extract dataset, loading, and error states from Redux store
  const projects = datasets?.[DATASET_NAME]?.["2020- present"] || []; //Get data for the specific dataset
  const { selectedFilters, searchQuery } = useSelector(
    (state) => state.searchFilter
  );
  const [page, setPage] = useState(1);
  const itemsPerPage = 4;

  // Fetch dataset when the component is mounted or lessons learned data changes
  useEffect(() => {
    if (!projects.length) {
      dispatch(fetchDataRequest(DATASET_NAME));
    }
  }, [dispatch, projects]);

  // Handle changes to filters (update Redux state with selected filters)
  const handleFilterChange = (category, value, dataset) => {
    dispatch(setSelectedFilters({ category, value, dataset }));
  };

  // Handle changes to the search query (update Redux state and reset pagination to first page)
  const handleSearchChange = (event) => {
    const newQuery = event.target.value;
    dispatch(searchQueryChanged(newQuery));
    setPage(1);
  };

  // Filter dataset based on selected filters and search query
  const filteredProjects = projects.filter((project) => {
    const datasetFilters = selectedFilters[DATASET_NAME];
    const matchesSelectedFilters = datasetFilters
      ? Object.keys(datasetFilters).every((category) => {
          if (!datasetFilters[category]?.length) return true; // Skip if no filters are selected for the category
          const projectValue = project[category] || "";
          return datasetFilters[category].some((filterValue) =>
            projectValue.includes(filterValue)
          );
        })
      : true;

    const matchesSearchQuery =
      project["Short description"]
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      project["Project's name"]
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());

    return matchesSelectedFilters && matchesSearchQuery;
  });

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProjects = filteredProjects.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleResetFilters = (dataset) => {
    dispatch(resetFiltersAndSearch(dataset));
  };

  return (
    <Box className={styles.mainContainer}>
      <FilterSidebar
        dataset={DATASET_NAME}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
      />

      <Box className={styles.contentArea}>
        <SearchBar
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
        />

        {loading ? (
          <Typography>Loading...</Typography>
        ) : error ? (
          <Typography color="error">Error: {error}</Typography>
        ) : (
          paginatedProjects.map((project, index) => (
            <Card key={index} variant="outlined" className={styles.contentCard}>
              <CardContent>
                <Typography variant="h6">
                  {project["Project's name"]}
                </Typography>
                <Typography>{project["Short description"]}</Typography>
                <Typography>
                  <strong>Project's duration:</strong>{" "}
                  {project["Project's duration"]}
                </Typography>
                <Button
                  href={project["CORDIS link to the project"]}
                  target="_blank"
                  color="secondary"
                >
                  More info
                </Button>
              </CardContent>
            </Card>
          ))
        )}

        <Pagination
          count={Math.ceil(filteredProjects.length / itemsPerPage)}
          page={page}
          onPageChange={handlePageChange}
        />
      </Box>
    </Box>
  );
};

export default ProjectList;
